<template>
  <div class="confirmation-home">
    <confirmation-band
      :isDelivery="isDelivery"
      :order-date="orderDate"
      :order-number="orderNumber"
      :scheduled-date="scheduledDate"
    />
    <div class="cards">
      <confirmation-destination
        :is-delivery="isDelivery"
        :google-static-map-url="googleStaticMapUrl"
      >
        <template slot="address">
          <div v-if="isDelivery">
            <h5 class="m-0 text-uppercase">
              {{ deliveryInfo.deliveryAddress.addressName }}
            </h5>
            <div>{{ deliveryInfo.deliveryAddress.address1 }}</div>
          </div>
          <div v-else>
            <h5 class="m-0 text-uppercase">{{ restaurantAddress }}</h5>
            <h5 class="m-0 text-uppercase">{{ restaurantAddressSecond }}</h5>
          </div>
        </template>
        <template slot="content">
          <div v-if="isDelivery" class="delivery-instructions text-left">
            <div v-if="deliveryInfo.deliveryAddress.instructions">
              <h6>Delivery Instructions</h6>
              <div>{{ deliveryInfo.deliveryAddress.instructions }}</div>
            </div>
          </div>
          <a
            v-else
            target="_blank"
            class="gold-link"
            :href="googleMapDirectionsUrl"
          >
            GET DIRECTIONS
          </a>
        </template>
      </confirmation-destination>
      <login
        v-show="$store.getters.isGuest"
        @login-success="loginSuccess"
        @forgot-password-click="forgotPassword"
        @create-an-account-click="isCreateAccountVisible = true"
      />
      <contact-info
        :email="contactInfo.email"
        :firstName="contactInfo.firstName"
        :isDelivery="isDelivery"
        :lastName="contactInfo.lastName"
        :phone="contactInfo.phone"
        :extension="contactInfo.extension"
      />
      <order-summary
        :total="orderGrandTotal"
        :orderTotal="orderTotal"
        :tax="orderTax"
        :deliveryFee="deliveryFee"
        :deliveryTax="deliveryTax"
        :order-items="mealSummaryItems"
        :restaurant-menu-data="restaurantMenuData"
        :isDelivery="currentOrder.orderDetail.isDelivery"
        :deliveryTip="deliveryTip"
      />
      <div v-if="isEditable">
        <div class="edit-order text-center">
          <div class="price-change-warning-text">
            Heads up, pricing may change if you edit this order.
          </div>
          <b-button variant="primary" @click.stop="editOrder">
            Edit Order
          </b-button>
        </div>
        <div class="d-block pb-3 pt-3 text-center">
          <span
            class="gold-link"
            role="button"
            tabindex="0"
            @keydown.enter="showConfirmCancelOrder = true"
            @click="showConfirmCancelOrder = true"
          >
            CANCEL ORDER
          </span>
        </div>
      </div>
      <div class="need-to-edit text-center" v-else>
        <div class="need-to-edit-title">NEED TO EDIT YOUR ORDER?</div>
        <div class="need-to-edit-description">
          <div v-if="$store.getters.isGuest">
            Login or create an account to edit this order.
            <br />
            Alternatively, please call us.
          </div>
          <div v-else>
            You're not able to edit your order online
            <br />
            within 24 hours of its pickup/delivery time.
            <br />
            Please call us to edit this order.
          </div>
        </div>
        <a :href="customerServicePhone" class="gold-link">1-800-CHIPOTLE</a>
      </div>
    </div>

    <b-modal
      v-model="showConfirmCancelOrder"
      body-class="confirm-cancel-body"
      centered
      hide-footer
      no-close-on-backdrop
      @hide="showConfirmCancelOrder = false"
    >
      <div slot="modal-title">
        CANCEL ORDER
        <span class="confirm-cancel-order-number">#{{ orderNumber }}</span>
      </div>
      <div class="text-center">
        <pepper-svg />
        <h3>CANCEL YOUR ORDER?</h3>
        <p class="confirm-cancel-body-msg">
          Are you sure you want to cancel your order?
          <br />
          This cannot be undone.
        </p>
        <div class="mb-3 ml-3 mr-3 row">
          <b-button
            type="button"
            block
            class="col-12 col-lg-8 offset-lg-2"
            size="lg"
            variant="primary"
            @click="cancelOrder"
          >
            <span>YES, CANCEL THIS ORDER</span>
          </b-button>
        </div>
        <div
          class="gold-link confirm-cancel-cancel-link"
          role="button"
          tabindex="0"
          @keydown.enter="showConfirmCancelOrder = false"
          @click="showConfirmCancelOrder = false"
        >
          NO, I CHANGED MY MIND. TAKE ME BACK TO ORDER DETAILS
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="showInEditOrder"
      body-class="confirm-cancel-body"
      centered
      hide-footer
      @hide="showInEditOrder = false"
    >
      <div slot="modal-title">EDIT ORDER</div>
      <div class="text-center">
        <pepper-svg />
        <h3>EDIT ALREADY IN PROGRESS</h3>
        <p class="confirm-cancel-body-msg">
          You are currently editing another order. Please submit or close that
          order before you attempt to edit another.
        </p>
        <div class="mb-3 ml-3 mr-3 row">
          <b-button
            type="button"
            block
            class="col-12 col-lg-8 offset-lg-2"
            size="lg"
            variant="primary"
            @click="goToExistingEditOrder"
          >
            <span>GO TO EXISTING EDIT ORDER</span>
          </b-button>
        </div>
        <div
          class="gold-link confirm-cancel-cancel-link"
          @click="showInEditOrder = false"
        >
          TAKE ME BACK TO ORDER DETAILS
        </div>
      </div>
    </b-modal>

    <create-an-account
      v-model="isCreateAccountVisible"
      @account-created="loginSuccess"
    />
  </div>
</template>

<script>
import ConfirmationBand from '@/components/confirmation/ConfirmationBand'
import ContactInfo from '@/components/confirmation/ContactInfo'
import OrderSummary from '@/components/confirmation/OrderSummary'
import Login from '@/components/confirmation/Login'
import CreateAnAccount from '@/components/account/CreateAnAccount'
import {
  CANCEL_SUBMITTED_ORDER,
  TRANSFER_ORDER,
  MARK_SUBMITTED_ORDER_FOR_UPDATE,
} from '@/store/mutation-types/orderMutations'
import { SHOW_CONFIRM_DIALOG } from '@/store/mutation-types/confirmDialogMutations'
import GoogleService from '@/services/googleMapsService'
import ConfirmationDestination from '@/components/confirmation/ConfirmationDestination'
import { goToRooPasswordReset } from '@/common/rooUtils'
import PepperSvg from '@/components/common/PepperSvg'
import { ORDER_HISTORY, CHECKOUT, HOME } from '@/router/routeNames'
import { getErrorMessage } from '@/common/errorCodeMapper'
import {
  GET_RESTAURANT_EPOPS,
  SHOW_EPOPS_MODAL,
} from '@/store/mutation-types/epopsMutations'
import { getDateInTimeZone } from '@/utilityFunctions'

export default {
  components: {
    ConfirmationBand,
    ConfirmationDestination,
    ContactInfo,
    Login,
    OrderSummary,
    PepperSvg,
    CreateAnAccount,
  },
  computed: {
    contactInfo() {
      return this.isDelivery
        ? this.deliveryInfo.deliveryAddress
        : this.pickupInfo
    },
    deliveryInfo() {
      return this.currentOrder.orderDetail.deliveryInfo
    },
    googleMapDirectionsUrl() {
      return `https://www.google.com/maps/dir/?api=1&destination=${this.restaurantLatitude},${this.restaurantLongitude}`
    },
    isDelivery() {
      return this.currentOrder.orderDetail.isDelivery
    },
    mealSummaryItems() {
      return this.$store.getters.submittedOrderItemsWithMenuData
    },
    orderDate() {
      return new Date(this.currentOrder.createdTimestamp)
    },
    orderNumber() {
      return this.currentOrder.orderNumber
    },
    orderTax() {
      const { foodTax } = this.currentOrder.orderDetail
      return foodTax || 0
    },
    deliveryTip() {
      const { deliveryInfo } = this.currentOrder.orderDetail
      return (deliveryInfo && deliveryInfo.deliveryTip) || 0
    },
    deliveryFee() {
      const { deliveryInfo } = this.currentOrder.orderDetail
      return (deliveryInfo && deliveryInfo.deliveryFee) || 0
    },
    deliveryTax() {
      const { deliveryInfo } = this.currentOrder.orderDetail
      return (deliveryInfo && deliveryInfo.deliveryTax) || 0
    },
    orderTotal() {
      return this.currentOrder.orderDetail.subTotal
    },
    orderGrandTotal() {
      return this.currentOrder.orderDetail.total
    },
    pickupInfo() {
      return this.currentOrder.orderDetail.pickupInfo
    },
    restaurantAddress() {
      return (
        this.restaurantAddressData && this.restaurantAddressData.addressLine1
      )
    },
    restaurantAddressData() {
      return this.restaurant.addresses && this.restaurant.addresses.length
        ? this.restaurant.addresses[0]
        : null
    },
    restaurantAddressSecond() {
      return (
        this.restaurantAddressData &&
        `${this.restaurantAddressData.locality}, ${this.restaurantAddressData.administrativeArea} ${this.restaurantAddressData.postalCode}`
      )
    },
    restaurantLatitude() {
      return this.restaurantAddressData && this.restaurantAddressData.latitude
    },
    restaurantLongitude() {
      return this.restaurantAddressData && this.restaurantAddressData.longitude
    },
    restaurantMenuData() {
      return this.$store.getters.confirmationRestaurantMenu
    },
    scheduledDate() {
      const resultDate = this.isDelivery
        ? new Date(this.currentOrder.orderDetail.deliveryInfo.deliveryDate)
        : new Date(this.currentOrder.orderDetail.pickupInfo.pickupDate)

      return getDateInTimeZone(
        resultDate,
        this.currentOrder.orderDetail.store.timezoneId,
      )
    },
    isEditable() {
      return (
        this.$store.getters.isSubmittedOrderChangeable &&
        !this.$store.getters.isGuest
      )
    },
    customerServicePhone() {
      return 'tel:+1-800-244-7685'
    },
  },
  data: (_) => ({
    currentOrder: {},
    restaurant: {},
    googleStaticMapUrl: '',
    showConfirmCancelOrder: false,
    showInEditOrder: false,
    isCreateAccountVisible: false,
  }),
  beforeMount() {
    this.currentOrder = this.$store.getters.submittedOrder
    this.restaurant = this.$store.getters.confirmationRestaurant

    let lat, long
    if (this.isDelivery) {
      lat = this.currentOrder.orderDetail.deliveryInfo.deliveryAddress.latitude
      long = this.currentOrder.orderDetail.deliveryInfo.deliveryAddress
        .longitude
    } else {
      lat = this.restaurantLatitude
      long = this.restaurantLongitude
    }

    GoogleService.url(lat, long, this.isDelivery).then((response) => {
      this.googleStaticMapUrl = response.data
    }) // TODO handle error and show proper error message, needs design and copy from PM/Deloitte and user story
  },
  methods: {
    editOrder() {
      // do not allow editing of order if there is already
      // an order being edited.
      if (this.$store.getters.isInEdit) {
        this.showInEditOrder = true
        return
      }

      this.$store
        .dispatch(
          MARK_SUBMITTED_ORDER_FOR_UPDATE,
          this.$store.state.route.params.orderId,
        )
        .then(() => {
          return this.$store
            .dispatch(GET_RESTAURANT_EPOPS, this.restaurant.restaurantNumber)
            .catch(() => {})
        })
        .then(() => {
          const afterComplete = () => {
            if (this.$store.getters.hasUnreadEpops) {
              this.$store.dispatch(SHOW_EPOPS_MODAL, true)
            }
          }
          this.$router.push({ name: CHECKOUT }, afterComplete)
        })
        .catch((e) =>
          this.handleError(
            e,
            'Unable to Edit Order',
            'An unexpected error occured while editing the order.',
          ),
        )
    },
    goToExistingEditOrder() {
      this.$router.push({ name: CHECKOUT })
    },
    cancelOrder() {
      this.$store
        .dispatch(
          CANCEL_SUBMITTED_ORDER,
          this.$store.state.route.params.orderId,
        )
        .then(() => {
          if (this.$store.getters.isFullUser) {
            this.$router.push({ name: ORDER_HISTORY })
          } else {
            this.$router.push({ name: HOME })
          }
        })
        .catch((e) =>
          this.handleError(
            e,
            'Unable to Cancel Order',
            'An unexpected error occured while canceling the order.',
          ),
        )
    },
    loginSuccess() {
      // Transfer the submitted order to this user
      this.$store.dispatch(
        TRANSFER_ORDER,
        this.$store.state.route.params.orderId,
      )
    },
    forgotPassword() {
      goToRooPasswordReset()
    },
    handleError(e, headerText, textValue) {
      let text, subText

      const errorCodeMessage = getErrorMessage(e)

      if (errorCodeMessage) {
        text = errorCodeMessage.text
        subText = errorCodeMessage.subText
      } else {
        text = textValue
        subText = 'Please try again or contact customer support.'
      }

      this.$store.dispatch(SHOW_CONFIRM_DIALOG, {
        text,
        subText,
        headerText,
        hideCancel: true,
      })
    },
  },
}
</script>

<style lang="scss">
@import '~@/styles/mixins/links.scss';
#content {
  .confirmation-home {
    background-color: $catering-bg-grey;
  }
}

.confirmation-home {
  .gold-link {
    @extend %gold-link;
    font-size: 18px;
    margin-bottom: 0;
  }
  .confirm-cancel-body {
    background-color: $catering-bg-grey;
  }
  .confirm-cancel-cancel-link {
    font-size: 13px;
  }
  .confirm-cancel-order-number {
    font-family: $font-family-helvetica;
    font-size: 14px;
    font-weight: 500;
  }
  .confirm-cancel-body-msg {
    margin-bottom: 15%;
  }
  .cards {
    margin: 15px;
  }
  .card {
    margin: 15px auto;
  }
  .pepper {
    color: $catering-white;
    margin: 5% auto;
    width: 56px;
    height: 73px;
  }
  .edit-order {
    padding-top: 1rem;
    padding-bottom: 1rem;

    button {
      height: 44px;
      width: 86%;
    }
  }
  .need-to-edit {
    text-align: center;
    padding: 20px 0;

    .need-to-edit-title {
      font-size: 26px;
      font-weight: bold;
      font-family: $headings-font-family;
      color: $catering-chocolate;
      letter-spacing: -0.37px;
    }

    .need-to-edit-description {
      font-size: 13px;
      font-family: $font-family-catering-text;
      color: $catering-medium-brown;
      padding: 15px 0 13px 0;
    }
  }
}

.delivery-instructions {
  h6 {
    font-size: 13px;
    font-family: $font-family-helvetica;
  }
  font-size: 13px;
}

.price-change-warning-text {
  font-family: $font-family-sans-serif;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  color: $catering-chocolate;
  text-transform: uppercase;
  margin-bottom: 1rem;
}
@include media-breakpoint-up(lg) {
  .confirmation-home {
    .cards {
      margin: 0 15px;
    }
    .card {
      width: 600px;
    }
    .confirm-cancel-cancel-link {
      font-size: 16px;
    }
    .edit-order button {
      width: 430px;
    }
  }
  .delivery-instructions {
    margin-left: 3rem;
    margin-right: 3rem;
  }
}
</style>
